import { CATEGORY, COLOR, COLUMN, FORMATTING, JARGON, LAYOUT } from "../../modules/constants"
import { addPaddingRightToLastCell, calcStats, getCell, getCellWithTooltip, headCell, setStats } from "../../modules/table.funcs"
import { initStats2 } from "../../components/layout/InitStats"
import { buildDateValues } from "./CreateLseCommonTableData"
import { getIntDate, normalizeS3Link } from "../../modules/utilities"

const { border1, integer, left, string } = FORMATTING
const borderLeft = border1
const stats2 = {}

export const createAdvisersHeadersData = props => {
    // console.log('createAdvisersHeadersData props:', props)
    const { portrait, showLogos, the350club } = props
    const lastWidth = 120
    const nextWidth = 135

    const starHead = headCell({
        align: left,
        id: COLUMN.STAR,
        width: LAYOUT.STAR_WIDTH,
    })

    const logo = headCell({
        align: left,
        id: COLUMN.LOGO,
        width: LAYOUT.LOGO_WIDTH,
    })

    const company = headCell({
        align: left,
        id: COLUMN.COMPANY,
        label: "Company",
        tooltipTitle: "",
    })

    const auditor = headCell({
        align: left,
        id: COLUMN.AUDITOR,
    })

    const recruitingAdviser = headCell({
        align: left,
        id: COLUMN.RECRUITING_ADVISER,
        paddingLeft: LAYOUT.COLUMN_PADDING,
    })

    const remunerationAdviser = headCell({
        align: left,
        id: COLUMN.REMUNERATION_ADVISER,
        paddingLeft: LAYOUT.COLUMN_PADDING,
    })

    const externalReview = headCell({
        align: left,
        borderLeft,
        paddingLeft: LAYOUT.COLUMN_PADDING,
        id: COLUMN.EXTERNAL_REVIEW,
    })

    const last = headCell({
        id: COLUMN.LAST,
        width: lastWidth,
    })

    const next = headCell({
        id: COLUMN.NEXT,
        width: nextWidth,
    })

    let headCells = portrait
        ? [starHead, company, externalReview, next]
        : [starHead, company, auditor, recruitingAdviser, remunerationAdviser, externalReview, last, next]

    if (showLogos)
        headCells.splice(1, 0, logo) // insert logo header

    if (the350club)
        headCells.shift() // Remove starHead

    addPaddingRightToLastCell(headCells)
    // console.log('headCells:', headCells)
    return headCells
}

export const createAdvisersFooterData = props => {
    // console.log('createAdvisersFooterData props:', props)
    const { portrait, showLogos, the350club } = props
    let column = null
    const tooltip = ""

    for (const property in stats2)
        calcStats(stats2[property])

    //#region  column cells
    column = COLUMN.COMPANY
    const suffix = ` ${stats2[column].total === 1 ? JARGON.COMPANY : JARGON.COMPANIES}`
    const company = {
        total: getCell({ align: left, column, suffix, tooltip, type: integer, value: stats2[column].total }),
        mean: getCell({ column })
    }

    column = COLUMN.STAR
    const starFoot = {
        total: getCell({ column }),
        mean: getCell({ column })
    }

    column = COLUMN.AUDITOR
    const auditor = {
        total: getCell({ column }),
        mean: getCell({ column })
    }

    column = COLUMN.RECRUITING_ADVISER
    const recruitingAdviser = {
        total: getCell({ column }),
        mean: getCell({ column })
    }

    column = COLUMN.REMUNERATION_ADVISER
    const remunerationAdviser = {
        total: getCell({ column }),
        mean: getCell({ column })
    }

    column = COLUMN.EXTERNAL_REVIEW
    const externalReview = {
        total: getCell({ column }),
        mean: getCell({ column })
    }

    column = COLUMN.LAST
    const last = {
        total: getCell({ column }),
        mean: getCell({ column })
    }

    column = COLUMN.NEXT
    const next = {
        total: getCell({ column }),
        mean: getCell({ column })
    }
    //#endregion column cells

    const footCells = {
        totals: portrait
            ? [starFoot.total, company.total, externalReview.total, next.total] // portrait
            : [starFoot.total, company.total, auditor.total, recruitingAdviser.total, remunerationAdviser.total, externalReview.total, last.total, next.total], // landscape
        // means: portrait
        //     ? [starFoot.mean, company.mean] // portrait
        //     : [starFoot.mean, company.mean, auditor.mean, recruitingAdviser.mean, remunerationAdviser.mean, externalReview.mean,  last.mean, next.mean], // landscape
    }

    if (showLogos) {
        const logoFoot = headCell({ id: COLUMN.LOGO, align: left })

        footCells.totals.splice(1, 0, logoFoot) // insert logo footer
        // footCells.means.splice(1, 0, logoFoot) // insert logo footer
    }

    if (the350club) {
        footCells.totals.shift() // Remove starFoot
        // footCells.means.shift()
    }

    addPaddingRightToLastCell(footCells.totals)
    // addPaddingRightToLastCell(footCells.means)
    // console.log('footCells:', footCells)
    return footCells
}

//#region Rows
export const createAdvisersRowData = props => {
    // console.log('createAdvisersRowData props:', props)
    const { items, stats } = props
    let rows = []
    const tooltipTitle = "" // JARGON.MORE_INFO

    initStats2(stats2)

    items.forEach(item => {
        rows.push(createAdviserRow({ ...props, item, tooltipTitle }))
        ++stats.numRows
    })
    // console.log('rows:', rows)
    return rows
}

const createAdviserRow = ({ item, portrait, showLogos }) => {

    const { advisers, annualReport } = item
    const endYearMonth = getIntDate(annualReport?.dates?.last?.doc).getMonth() ?? 0 // Default to January if month is not available
    const lastReview = buildDateValues(endYearMonth, advisers?.lastReviewEndYear)
    const nextReview = buildDateValues(endYearMonth, advisers?.nextReviewEndYear)

    const cellSpec = {

        [COLUMN.COMPANY]: {
            value: 1,
        },
        [COLUMN.NAME]: {
            align: left,
            type: string,
            value: item.names[0],
        },
        [COLUMN.AUDITOR]: {
            type: string,
            value: advisers?.auditor ?? "",
        },
        [COLUMN.RECRUITING_ADVISER]: {
            paddingLeft: LAYOUT.COLUMN_PADDING,
            type: string,
            value: advisers?.recruitingAdviser ?? "",
        },
        [COLUMN.REMUNERATION_ADVISER]: {
            paddingLeft: LAYOUT.COLUMN_PADDING,
            type: string,
            value: advisers?.remunerationAdviser ?? "",
        },
        [COLUMN.EXTERNAL_REVIEW]: {
            borderLeft,
            link: normalizeS3Link(advisers?.urlToMentionOfReviewInAr),
            newTab: true,
            paddingLeft: LAYOUT.COLUMN_PADDING,
            type: string,
            value: advisers?.lastReviewFacilitator ?? "",
        },
        [COLUMN.LAST]: {
            sortValue: lastReview.sort,
            value: lastReview.display,
        },
        [COLUMN.NEXT]: {
            color: nextReview.status === 1 ? COLOR.GOOD : nextReview.status === 2 ? COLOR.WARN : null,
            sortValue: nextReview.sort,
            value: nextReview.display,
        },
    }

    // NB: 'setStats()' accepts both a number or bool for its 1st parameter
    // since the unary '+' operator converts boolean into a number
    // @ts-ignore
    setStats(cellSpec[COLUMN.COMPANY].value, stats2[COLUMN.COMPANY], true)

    const name = getCellWithTooltip(cellSpec[COLUMN.NAME])
    const auditor = getCellWithTooltip(cellSpec[COLUMN.AUDITOR])
    const recruitingAdviser = getCellWithTooltip(cellSpec[COLUMN.RECRUITING_ADVISER])
    const remunerationAdviser = getCellWithTooltip(cellSpec[COLUMN.REMUNERATION_ADVISER])
    const externalReview = getCellWithTooltip(cellSpec[COLUMN.EXTERNAL_REVIEW])
    const last = getCellWithTooltip(cellSpec[COLUMN.LAST])
    const next = getCellWithTooltip(cellSpec[COLUMN.NEXT])

    const logo = item.logo
        ? item.logo
        : "no logo"

    const cellWithTooltips = portrait
        ? [name, externalReview, next] // portrait
        : [name, auditor, recruitingAdviser, remunerationAdviser, externalReview, last, next] // landscape

    addPaddingRightToLastCell(cellWithTooltips)

    return {
        _id: item.ids[0],
        category: CATEGORY.COMPANY,
        cellWithTooltips,
        iconColor: COLOR.GOOD,
        ids: item.ids,
        index: item.index,
        isFavourite: item.isFavourite,
        link: `/company/${item.ids[0]}`,
        logo: showLogos ? logo : null, // insert logo
        title: item.companyName,
    }
}
//#endregion Rows

